import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppPagination from '../../../components/AppPagination';
import AppActionMenu from '../../../components/AppActionMenu';
import ToolsButton from '../../../components/buttons/ToolsButton';
import { useSourcesListScreen } from './hooks/useSourcesListScreen';

export default function SourcesListScreen() {
  const { state, actions } = useSourcesListScreen();
  const navigate = useNavigate();

  const statusTabs = [
    { key: 'active', label: 'Active' },
    { key: 'archived', label: 'Archived' },
    { key: 'all', label: 'All' },
  ];

  const toolsOptions = [
    { key: 'print', label: 'Print Report', icon: 'print' },
    { key: 'export_excel', label: 'Export to Excel', icon: 'file_download' },
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Banner */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-5">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant mb-1">
            <span>Masters</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span>Admissions & Marketing</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span className="text-primary font-bold">Source Master</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Lead & Inquiry Source Registry
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">
            Configure marketing acquisition channels for prospect admissions, campaign tracking, and CRM attribution.
          </p>
        </div>
      </section>

      {/* Filter & Actions Row */}
      <section className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Search & Reset */}
        <div className="flex flex-wrap items-center gap-3 w-full max-w-xl">
          <label className="relative flex-1 min-w-[240px]">
            <span className="material-symbols-outlined pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-outline text-lg">
              search
            </span>
            <input
              type="text"
              value={state.searchText}
              onChange={(event) => actions.setSearchText(event.target.value)}
              placeholder="Search by source name or code..."
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 pl-10 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
            />
          </label>

          {state.searchText && (
            <button
              type="button"
              onClick={actions.resetFilters}
              className="inline-flex items-center gap-1 shrink-0 rounded-xl bg-surface-container px-3.5 py-2.5 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer"
            >
              <span className="material-symbols-outlined text-sm">filter_alt_off</span>
              <span>Clear</span>
            </button>
          )}
        </div>

        {/* Status Filter Tabs & Tools (No local Add Button per standards) */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-surface-container-high w-fit shadow-xs">
            {statusTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => actions.setViewMode(tab.key)}
                className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer select-none ${
                  state.viewMode === tab.key
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest/60'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <ToolsButton
            tools={toolsOptions}
            onSelect={actions.handleToolSelect}
            disabled={state.isExporting}
            triggerClassName={`h-9 px-3.5 bg-surface-container-highest hover:bg-outline-variant/40 text-on-surface-variant font-bold rounded-xl text-xs flex items-center gap-2 cursor-pointer transition select-none shadow-2xs ${
              state.isExporting ? 'opacity-60 pointer-events-none' : ''
            }`}
          />
        </div>
      </section>

      {/* Main Table Card */}
      <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest overflow-hidden shadow-xs flex flex-col">
        {/* Table Header */}
        <div className="grid grid-cols-12 px-6 py-3.5 border-b border-outline-variant/20 bg-surface-container-low/50 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface-variant select-none">
          <div className="col-span-6 md:col-span-5">Source Details</div>
          <div className="col-span-4 md:col-span-5">Abbreviation / Code</div>
          <div className="col-span-1 text-center">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {/* Loading State */}
        {state.loading && (
          <div className="flex flex-col items-center justify-center p-12 text-on-surface-variant gap-3">
            <span className="material-symbols-outlined animate-spin text-3xl text-primary">
              progress_activity
            </span>
            <span className="text-xs font-semibold">Loading sources...</span>
          </div>
        )}

        {/* Empty State */}
        {!state.loading && state.rows.length === 0 && (
          <div className="flex flex-col items-center justify-center p-12 text-center text-on-surface-variant gap-3">
            <div className="w-12 h-12 rounded-full bg-surface-container-highest flex items-center justify-center text-outline">
              <span className="material-symbols-outlined text-2xl">campaign</span>
            </div>
            <div>
              <p className="text-sm font-bold text-on-surface">No sources found</p>
              <p className="text-xs text-on-surface-variant mt-1">
                {state.searchText
                  ? 'Try adjusting your search criteria or clearing filters.'
                  : 'Get started by creating a new source via the "+ Create New" button in the top navigation bar.'}
              </p>
            </div>
          </div>
        )}

        {/* Data Rows */}
        {!state.loading && state.rows.length > 0 && (
          <div className="divide-y divide-outline-variant/10">
            {state.rows.map((row) => (
              <div
                key={row.id}
                onClick={() => navigate(`/masters/sources/entry/${row.id}`)}
                className="grid grid-cols-12 px-6 py-3.5 items-center hover:bg-surface-container-high/40 transition-colors cursor-pointer group"
              >
                {/* Source Name & ID */}
                <div className="col-span-6 md:col-span-5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-base">ads_click</span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-on-surface truncate group-hover:text-primary transition-colors">
                      {row.name}
                    </div>
                    <div className="text-[10px] text-on-surface-variant/80 font-medium">
                      ID: #{row.id}
                    </div>
                  </div>
                </div>

                {/* Abbreviation */}
                <div className="col-span-4 md:col-span-5">
                  {row.short_name ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg bg-surface-container text-xs font-semibold text-on-surface-variant">
                      {row.short_name}
                    </span>
                  ) : (
                    <span className="text-xs text-on-surface-variant/50">—</span>
                  )}
                </div>

                {/* Status Chip */}
                <div className="col-span-1 flex justify-center">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold capitalize select-none ${
                      row.status === 'Active'
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                        : 'bg-surface-container-highest text-on-surface-variant'
                    }`}
                  >
                    {row.status}
                  </span>
                </div>

                {/* Actions Menu */}
                <div
                  className="col-span-1 flex justify-end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AppActionMenu
                    triggerIcon="more_vert"
                    triggerLabel=""
                    showChevron={false}
                    triggerClassName="w-8 h-8 rounded-lg text-on-surface-variant hover:bg-surface-container-high flex items-center justify-center p-0 cursor-pointer"
                    align="end"
                    items={[
                      {
                        key: 'edit',
                        label: 'Edit Source',
                        icon: 'edit',
                      },
                      {
                        key: 'archive',
                        label: row.status === 'Active' ? 'Archive Source' : 'Restore Source',
                        icon: row.status === 'Active' ? 'archive' : 'unarchive',
                      },
                      {
                        key: 'delete',
                        label: 'Delete Source',
                        icon: 'delete',
                        variant: 'danger',
                      },
                    ]}
                    onSelect={(key) => {
                      if (key === 'edit') {
                        navigate(`/masters/sources/entry/${row.id}`);
                      } else if (key === 'archive') {
                        actions.toggleArchive(row);
                      } else if (key === 'delete') {
                        actions.deleteItem(row);
                      }
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer Pagination */}
        <div className="border-t border-outline-variant/15 p-4 bg-surface-container-lowest">
          <AppPagination
            currentPage={state.page}
            totalPages={state.totalPages}
            totalItems={state.totalCount}
            pageSize={state.pageSize}
            onPageChange={actions.setPage}
            onPageSizeChange={actions.setPageSize}
          />
        </div>
      </div>
    </div>
  );
}
